import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { FormErrorAlert } from '@/features/auth/FormErrorAlert';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { getErrorMessage } from '@/lib/api-error';
import {
  EMPTY_FORM_ERRORS,
  fieldError,
  formErrorsFromApiError,
  formErrorsFromFields,
  type FormErrors,
} from '@/lib/form-errors';
import { base64FromDataUrl, readFileAsDataUrl } from '@/lib/image-file';
import type { AttachGuideResult, GuideLabelReading, Order } from '@/types/orders';
import { orderStatus } from '@/theme/status';
import { describeGuideSend, formatNumber } from './orders-format';
import {
  ORDER_GUIDE_FORM_FIELDS,
  attachGuidePayload,
  emptyGuideForm,
  type GuideFileState,
  type GuideFormState,
} from './orders-payload';
import { ORDER_LIMITS, describeGuideLabelReading, validateGuideFile, validateGuideForm } from './orders-validation';
import { useReadGuideLabel } from './use-read-guide-label';
import { useAttachShippingGuide } from './use-orders';

/**
 * Carga de la foto de la guía de envío (`PATCH .../orders/:orderId/guia`, solo ADMIN).
 *
 * Aquí está la decisión más importante del módulo: **la guía ES el despacho**. Al guardarla, el
 * backend (1) pasa la orden a `ENVIADA` y deja la fila en el historial, (2) le manda al cliente la
 * foto con el número de guía como pie y (3) registra ese mensaje en el chat. Por eso el diálogo
 * explica qué va a pasar antes de guardar y, después, cuenta qué pasó de verdad: `envio.motivo`
 * distingue «el cliente ya la recibió» de «la guía quedó guardada pero no se pudo avisar».
 *
 * Solo se ofrece desde los estados que la aceptan (`canAttachGuide` en la pantalla): `CONFIRMADA`,
 * `EN_PREPARACION` y `ENVIADA` (re-subir la guía sirve para reintentar el aviso). El backend responde
 * 400 en `PENDIENTE_CONFIRMACION` (primero hay que confirmar la venta) y en los estados finales.
 *
 * Dos formas excluyentes de mandar la foto: subir el archivo (se lee con `FileReader` y viaja en
 * base64, máximo 5 MB comprobado ANTES de leerlo) o pegar una URL pública. Si hay archivo, se manda
 * el archivo (ver `attachGuidePayload`).
 */

export interface OrderGuideDialogProps {
  open: boolean;
  onClose: () => void;
  /** Orden a despachar (`null` = diálogo cerrado). */
  order: Order | null;
}

export function OrderGuideDialog({ open, onClose, order }: OrderGuideDialogProps) {
  const { tenantId } = useActiveTenantAccess();
  const attachGuide = useAttachShippingGuide(tenantId);

  const [form, setForm] = useState<GuideFormState>(emptyGuideForm);
  const [errors, setErrors] = useState<FormErrors>(EMPTY_FORM_ERRORS);
  const [submitError, setSubmitError] = useState<string | null>(null);
  /** Respuesta del backend cuando la guía ya se guardó (el diálogo se queda para contarlo). */
  const [resultado, setResultado] = useState<AttachGuideResult | null>(null);
  /** `true` mientras el navegador lee el archivo elegido (puede tardar con fotos grandes). */
  const [leyendo, setLeyendo] = useState(false);
  /** Cambia la `key` del input de archivo: es la forma de vaciarlo desde React (no es controlable). */
  const [fileInputKey, setFileInputKey] = useState(0);
  /** Última lectura de la etiqueta con Gemini (para precargar los campos y explicar qué pasó). */
  const [lectura, setLectura] = useState<GuideLabelReading | null>(null);
  const [lecturaError, setLecturaError] = useState<string | null>(null);
  const leerEtiqueta = useReadGuideLabel(tenantId);

  /** Cada apertura arranca limpia: ni el archivo ni el resultado de la vez anterior sobreviven. */
  useEffect(() => {
    if (!open) return;

    setForm(emptyGuideForm());
    setErrors(EMPTY_FORM_ERRORS);
    setSubmitError(null);
    setResultado(null);
    setLeyendo(false);
  }, [open]);

  /**
   * Pide a Gemini que LEA la etiqueta y precarga número de guía, transportadora y número de pedido.
   *
   * Es un extra, nunca un requisito: si el negocio no tiene Gemini, si la etiqueta no se lee o si la
   * llamada falla, se avisa y el admin escribe los datos a mano (el archivo ya está listo). Y **no pisa
   * lo que ya haya escrito**: cada campo solo se rellena si está vacío.
   */
  async function leerDatosDeLaEtiqueta(archivo: GuideFileState) {
    if (!order) return;

    setLectura(null);
    setLecturaError(null);

    try {
      const reading = await leerEtiqueta.mutateAsync({
        orderId: order.id,
        payload: { imageBase64: archivo.base64, mimeType: archivo.mimeType },
      });

      setLectura(reading);
      patch({
        numero: form.numero.trim() ? form.numero : (reading.numeroGuia ?? ''),
        transportadora: form.transportadora.trim()
          ? form.transportadora
          : (reading.transportadora ?? ''),
        ordenTransportadora: form.ordenTransportadora.trim()
          ? form.ordenTransportadora
          : (reading.ordenTransportadora ?? ''),
      });
    } catch (error) {
      // Un fallo de red/permisos: la lectura no está disponible, pero la guía se puede cargar igual.
      setLecturaError(getErrorMessage(error));
    }
  }

  function patch(changes: Partial<GuideFormState>) {
    setForm((current) => ({ ...current, ...changes }));
  }

  const guardando = attachGuide.isPending || leyendo;

  /** Quita el archivo elegido (vuelve a mandarse la URL, si hay). */
  function clearFile() {
    patch({ archivo: null });
    setFileInputKey((value) => value + 1);
    setErrors(EMPTY_FORM_ERRORS);
    setSubmitError(null);
    // La lectura era de ESA foto: sin archivo no hay nada que haya leído.
    setLectura(null);
    setLecturaError(null);
  }

  /**
   * Lee la foto elegida. El tope y el tipo se comprueban ANTES de leer (con el `size` y el `type`
   * que da el navegador): así no se convierte en base64 una foto de 12 MB que el backend rechaza.
   */
  async function handleFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null;
    if (!file) {
      patch({ archivo: null });
      return;
    }

    const candidato: GuideFileState = {
      name: file.name,
      base64: '',
      mimeType: file.type,
      size: file.size,
    };

    // `'pendiente'`: aquí solo se comprueban el tope y el tipo (el base64 todavía no existe). Exigirlo
    // en este punto hacía imposible elegir cualquier foto (bug del 20-sep-2026).
    const problema = validateGuideFile(candidato, 'pendiente');
    if (problema) {
      setErrors(formErrorsFromFields({ imageBase64: problema }));
      patch({ archivo: null });
      event.target.value = '';
      return;
    }

    setErrors(EMPTY_FORM_ERRORS);
    setSubmitError(null);
    setLectura(null);
    setLecturaError(null);
    setLeyendo(true);

    try {
      const dataUrl = await readFileAsDataUrl(file);
      const { base64, mimeType } = base64FromDataUrl(dataUrl);
      const archivo = {
        name: file.name,
        base64,
        mimeType: mimeType ?? file.type,
        size: file.size,
      };

      patch({ archivo });
      // Con el archivo listo se intenta LEER la etiqueta (número de guía, transportadora y número de
      // pedido). Es un extra: la guía se carga igual escribiéndolos a mano.
      void leerDatosDeLaEtiqueta(archivo);
    } catch {
      setSubmitError('No se pudo leer el archivo: elige la foto otra vez.');
      patch({ archivo: null });
      event.target.value = '';
    } finally {
      setLeyendo(false);
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!order) return;

    const nextErrors = validateGuideForm(form);
    if (Object.keys(nextErrors.fieldErrors).length > 0) {
      setErrors(nextErrors);
      setSubmitError(null);
      return;
    }

    setErrors(EMPTY_FORM_ERRORS);
    setSubmitError(null);

    try {
      // La respuesta trae la orden CON su historial (la carga deja una fila nueva), así que el
      // detalle abierto se refresca solo y aquí no hay que releer nada.
      const result = await attachGuide.mutateAsync({
        orderId: order.id,
        payload: attachGuidePayload(form),
      });
      setResultado(result);
    } catch (error) {
      setErrors(formErrorsFromApiError(error, ORDER_GUIDE_FORM_FIELDS));
      setSubmitError(getErrorMessage(error));
    }
  }

  if (!order) return null;

  const actual = orderStatus[order.estado];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={order.estado === 'ENVIADA' ? 'Reemplazar la guía de envío' : 'Cargar la guía de envío'}
      description={`${order.clienteNombre} · hoy está en «${actual.label}»`}
      footer={
        resultado ? (
          <Button onClick={onClose}>Cerrar</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose} disabled={guardando}>
              Cancelar
            </Button>
            <Button type="submit" form="order-guide-form" loading={guardando}>
              Guardar y avisar al cliente
            </Button>
          </>
        )
      }
    >
      <form id="order-guide-form" onSubmit={handleSubmit} noValidate className="space-y-4">
        <FormErrorAlert message={submitError} errors={errors} />

        {resultado ? (
          <div className="space-y-3">
            <Alert tone={resultado.envio.enviado ? 'success' : 'warning'}>
              <p className="font-medium">
                {resultado.cambioEstado
                  ? `La orden pasó de «${orderStatus[resultado.cambioEstado.anterior].label}» a «Enviada».`
                  : 'La guía quedó guardada (la orden ya estaba en «Enviada»).'}
              </p>
              <p className="mt-1">{describeGuideSend(resultado.envio)}</p>
            </Alert>

            <dl className="space-y-1 rounded-panel border border-line bg-surface-muted/40 px-4 py-3 text-caption">
              <div className="flex justify-between gap-4">
                <dt className="text-ink-muted">Número de guía</dt>
                <dd className="truncate text-ink">{resultado.order.guiaEnvioNumero ?? 'Sin número'}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-ink-muted">Foto guardada</dt>
                <dd className="truncate text-ink">{resultado.order.guiaEnvioFotoUrl ?? 'Sin foto'}</dd>
              </div>
              {resultado.almacenamiento ? (
                <div className="flex justify-between gap-4">
                  <dt className="text-ink-muted">Archivo en el servidor</dt>
                  <dd className="truncate text-ink">{resultado.almacenamiento.relativePath}</dd>
                </div>
              ) : null}
            </dl>

            {resultado.almacenamiento && !resultado.almacenamiento.publicUrl ? (
              <Alert tone="warning">
                La foto quedó guardada en el backend, pero su URL no es pública (falta
                `PUBLIC_BASE_URL`): Meta no puede descargarla, así que al cliente no le llegó la
                imagen. Configúrala y vuelve a cargar la guía para reenviarla.
              </Alert>
            ) : null}
          </div>
        ) : (
          <>
            <Alert tone="info">
              Al guardar, la orden pasa a «Enviada» y el cliente recibe en su chat la foto de la guía
              con el número. El mensaje queda registrado en la conversación.
            </Alert>

            <Field
              label="Foto de la guía"
              htmlFor="orderGuiaArchivo"
              hint="JPG o PNG de hasta 5 MB. Se lee en tu equipo y viaja al backend en base64."
              error={fieldError(errors, 'imageBase64')}
            >
              <Input
                key={fileInputKey}
                id="orderGuiaArchivo"
                name="imageBase64"
                type="file"
                accept="image/*"
                disabled={guardando}
                invalid={Boolean(fieldError(errors, 'imageBase64'))}
                onChange={(event) => void handleFile(event)}
              />
            </Field>

            {form.archivo ? (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-panel border border-line bg-surface-muted/40 px-3 py-2">
                <p className="min-w-0 truncate text-caption text-ink-soft">
                  {`${form.archivo.name} · ${formatNumber(Math.round(form.archivo.size / 1024))} KB · ${form.archivo.mimeType}`}
                </p>
                <Button variant="ghost" size="sm" disabled={guardando} onClick={clearFile}>
                  Quitar el archivo
                </Button>
              </div>
            ) : null}

            {/* Qué pasó con la lectura de la etiqueta (si se intentó). Nunca bloquea: se puede seguir
                escribiendo todo a mano. */}
            {lectura || lecturaError ? (
              <Alert tone={lectura?.status === 'ok' ? 'success' : 'info'}>
                {lecturaError ?? (lectura ? describeGuideLabelReading(lectura) : '')}
              </Alert>
            ) : null}

            {form.archivo && !lectura && !lecturaError ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                loading={leerEtiqueta.isPending}
                disabled={guardando}
                onClick={() => {
                  const archivo = form.archivo;
                  if (archivo) void leerDatosDeLaEtiqueta(archivo);
                }}
              >
                Leer los datos de la etiqueta
              </Button>
            ) : null}

            <Field
              label="…o la dirección de la guía (URL)"
              htmlFor="orderGuiaUrl"
              hint="Se ignora si subiste un archivo. Úsala cuando la transportadora te dé un enlace público."
              error={fieldError(errors, 'guiaEnvioFotoUrl')}
            >
              <Input
                id="orderGuiaUrl"
                name="guiaEnvioFotoUrl"
                placeholder="https://…"
                disabled={guardando || form.archivo !== null}
                value={form.fotoUrl}
                invalid={Boolean(fieldError(errors, 'guiaEnvioFotoUrl'))}
                onChange={(event) => patch({ fotoUrl: event.target.value })}
              />
            </Field>

            <Field
              label="Número de guía"
              htmlFor="orderGuiaNumero"
              hint="Va en el mensaje que recibe el cliente y queda en el detalle de la orden."
              error={fieldError(errors, 'guiaEnvioNumero')}
            >
              <Input
                id="orderGuiaNumero"
                name="guiaEnvioNumero"
                placeholder="Ej. GUIA-12345"
                maxLength={ORDER_LIMITS.guiaNumero}
                disabled={guardando}
                value={form.numero}
                invalid={Boolean(fieldError(errors, 'guiaEnvioNumero'))}
                onChange={(event) => patch({ numero: event.target.value })}
              />
            </Field>

            {/* Los dos campos que Gemini lee de la etiqueta (editables). La transportadora viaja al bot
                de postventa y al pie de foto con el que el cliente recibe la guía. */}
            <Field
              label="Transportadora"
              htmlFor="orderGuiaTransportadora"
              hint="Se lee de la etiqueta cuando Gemini está configurado. El bot de postventa la usa para responder."
              error={fieldError(errors, 'transportadora')}
            >
              <Input
                id="orderGuiaTransportadora"
                name="transportadora"
                placeholder="Ej. Interrapidísimo"
                maxLength={ORDER_LIMITS.guiaNumero}
                disabled={guardando}
                value={form.transportadora}
                invalid={Boolean(fieldError(errors, 'transportadora'))}
                onChange={(event) => patch({ transportadora: event.target.value })}
              />
            </Field>

            <Field
              label="Número de pedido de la transportadora"
              htmlFor="orderGuiaOrdenTransportadora"
              hint="La referencia que imprime la transportadora (opcional). Queda en la orden para postventa."
              error={fieldError(errors, 'ordenTransportadora')}
            >
              <Input
                id="orderGuiaOrdenTransportadora"
                name="ordenTransportadora"
                placeholder="Ej. 123456789"
                maxLength={ORDER_LIMITS.guiaNumero}
                disabled={guardando}
                value={form.ordenTransportadora}
                invalid={Boolean(fieldError(errors, 'ordenTransportadora'))}
                onChange={(event) => patch({ ordenTransportadora: event.target.value })}
              />
            </Field>
          </>
        )}
      </form>
    </Modal>
  );
}
